import { Stack } from '@mui/material';
import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { DetailedUserDto, MinimalCustomer, RoleDtoRole } from '@/generated/model';

import type { UserAccessFormDirtyFields, UserAccessFormValues } from '../../types/UserAccessFormValues';
import { getPatchAttributes } from '../../api/userMappers';
import { useUpdateUserMutation } from '../../api/userMutations';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { UserAccessForm } from '../user-access-form/UserAccessForm';

type EditUserFormContentProps = {
  userId: number;
  user: DetailedUserDto;
  initialValues: UserAccessFormValues;
  customers: MinimalCustomer[];
  roles: RoleDtoRole[];
  isSelfService: boolean;
  header: ReactElement;
  onCancel: () => void;
};

export function EditUserFormContent({
  userId,
  user,
  initialValues,
  customers,
  roles,
  isSelfService,
  header,
  onCancel,
}: EditUserFormContentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  async function onSuccess() {
    await navigate({ pathname: userAccessRoutes.root, search: location.search }, { state: location.state });
  }
  const patchUser = useUpdateUserMutation(userId, onSuccess);

  function handleSubmit(data: UserAccessFormValues, dirtyFields: UserAccessFormDirtyFields) {
    const attributes = getPatchAttributes(data, dirtyFields, isSelfService);
    if (Object.keys(attributes).length === 0) {
      return;
    }

    patchUser.mutate({
      data: {
        username: user.username,
        attributes,
      },
    });
  }

  return (
    <Stack width="100%" height="100%" minHeight={0} bgcolor="background.paper">
      {header}
      {/* React Hook Form applies initial values on mount, so changing users needs a fresh form instance. */}
      <UserAccessForm
        key={userId}
        mode={isSelfService ? 'self-service' : 'edit'}
        initialValues={initialValues}
        customers={customers}
        roles={roles}
        isPending={patchUser.isPending}
        onCancel={onCancel}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
}
